"use client";

import { lazy, Suspense, useEffect, useState } from "react";
import {
  confirmDismissGuards,
  hasActiveDismissGuards,
  setDismissConfirmation,
} from "src/lib/DismissGuards";
import { useNavigationGuard } from "src/lib/NavigationGuard";
import { type DismissGuardOptions, useDismissGuard } from "./useDismissGuard";

export { confirmDismissGuards } from "src/lib/DismissGuards";

const DismissGuardDialog = lazy(() => import("./DismissGuardDialog"));

/**
 * Asks before unsaved input is lost. A surface with unsaved changes calls `useDismissGuard`; while
 * it is dirty, closing its dialog, navigating within the app, reloading or leaving the page, and
 * any action that calls `confirmDismissGuards` (such as switching project) first ask whether to
 * discard the changes. `DismissGuardHost` (mounted once in the root layout) shows that question.
 */

/**
 * `useDismissGuard` as an element, for components that render their own dialog: place it inside
 * `Dialog.Content` so closing that dialog is guarded too.
 */
export const DismissGuard = (props: DismissGuardOptions) => {
  useDismissGuard(props);
  return null;
};

/** Mounted once in the root layout. */
export const DismissGuardHost = () => {
  const [pending, setPending] = useState<((discard: boolean) => void) | null>(null);
  // The dialog loads the first time something asks, not with every page.
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setDismissConfirmation(
      () =>
        new Promise<boolean>((resolve) => {
          setOpened(true);
          setPending(() => resolve);
        }),
    );
    return () => setDismissConfirmation(null);
  }, []);

  useNavigationGuard({
    enabled: ({ to }) => hasActiveDismissGuards(to),
    confirm: () => confirmDismissGuards(),
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasActiveDismissGuards()) event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const settle = (discard: boolean) => {
    pending?.(discard);
    setPending(null);
  };

  return opened ? (
    <Suspense fallback={null}>
      <DismissGuardDialog visible={pending !== null} settle={settle} />
    </Suspense>
  ) : null;
};
